import cn from 'classnames';
import { useCallback, useState } from 'react';

import ErrorMessage from 'components/common/ErrorMessage';
import StripeExpressCheckout from 'components/common/forms/StripeExpressCheckout';
import PaypalButtonSkeleton from 'components/common/PaypalButtonSkeleton';
import { paymentError } from 'lib/constants/paymentErrors';

const ApplePayCheckout = ({
  onApplePayClick,
  onApplePayComplete,
  onApplePayError,
  onApplePayAvailabilityChange,
}) => {
  const [applePayAvailable, setApplePayAvailable] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const showApplePayButton = applePayAvailable === true;
  const isApplePayLoading = applePayAvailable === null && !errorMessage;

  // Stripe reports whether Apple Pay is available on this device/browser.
  // Store it for local rendering and propagate it so parent layout can adjust.
  const handleApplePayReady = useCallback(
    ({ isApplePayAvailable }) => {
      setErrorMessage('');
      const isAvailable = Boolean(isApplePayAvailable);
      setApplePayAvailable(isAvailable);
      onApplePayAvailabilityChange?.(isAvailable);
    },
    [onApplePayAvailabilityChange]
  );

  // Reset stale Apple Pay errors before a new attempt and delegate click handling upstream.
  const handleApplePayClick = useCallback(async () => {
    setErrorMessage('');
    return onApplePayClick?.();
  }, [onApplePayClick]);

  // Show a user-facing fallback message and pass raw error up for analytics/logging.
  const handleApplePayError = useCallback(
    error => {
      setErrorMessage(paymentError('Apple Pay'));
      onApplePayError?.(error);
    },
    [onApplePayError]
  );

  return (
    <>
      {errorMessage && <ErrorMessage message={errorMessage} />}
      {isApplePayLoading && (
        <div className="relative min-h-[50px] w-full">
          <div data-testid="apple-pay-loading">
            <PaypalButtonSkeleton height={50} rounded="full" />
          </div>
        </div>
      )}
      <div
        className={cn({
          'min-h-[50px] w-full min-w-44': showApplePayButton,
          'pointer-events-none absolute left-0 top-0 h-0 w-0 overflow-hidden': !showApplePayButton,
        })}
        aria-hidden={!showApplePayButton}
      >
        <StripeExpressCheckout
          onReady={handleApplePayReady}
          onComplete={onApplePayComplete}
          onError={handleApplePayError}
          onClick={handleApplePayClick}
        />
      </div>
    </>
  );
};

ApplePayCheckout.propTypes = {
  onApplePayClick: PropTypes.func,
  onApplePayComplete: PropTypes.func.isRequired,
  onApplePayError: PropTypes.func,
  onApplePayAvailabilityChange: PropTypes.func,
};

export default ApplePayCheckout;
